/**
 * JWT utility functions for handling JWT tokens and scopes
 */

import type { JWTPayload } from "@/types/auth"

/**
 * Decode JWT token without verification (client-side only)
 * Note: This is for reading payload, not for security validation
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    // Split token into parts
    const parts = token.split(".")
    if (parts.length !== 3) {
      return null
    }

    // Decode payload (second part)
    const payload = parts[1]

    // Add padding if needed for base64 decoding
    const padded = payload + "=".repeat((4 - (payload.length % 4)) % 4)

    // Decode base64
    const decoded = atob(padded.replace(/-/g, "+").replace(/_/g, "/"))

    // Parse JSON
    const parsed = JSON.parse(decoded) as JWTPayload

    return parsed
  } catch (error) {
    console.error("Failed to decode JWT token:", error)
    return null
  }
}

/**
 * Extract scopes from JWT token
 */
export function extractScopesFromToken(token: string): string[] {
  const payload = decodeJWT(token)
  console.log("[JWT] Decoded payload:", payload)
  console.log("[JWT] Scopes in payload:", payload?.scopes)
  return payload?.scopes || []
}

/**
 * Extract user ID from JWT token
 */
export function extractUserIdFromToken(token: string): string | null {
  const payload = decodeJWT(token)
  return payload?.sub || null
}

/**
 * Extract email from JWT token
 */
export function extractEmailFromToken(token: string): string | null {
  const payload = decodeJWT(token)
  return payload?.email || null
}

/**
 * Check if JWT token is expired
 */
export function isTokenExpired(token: string): boolean {
  const payload = decodeJWT(token)
  if (!payload || !payload.exp) {
    return true
  }

  // Check if token is expired (with 30 second buffer)
  const now = Math.floor(Date.now() / 1000)
  return payload.exp < now + 30
}

/**
 * Get token expiry timestamp
 */
export function getTokenExpiry(token: string): number | null {
  const payload = decodeJWT(token)
  return payload?.exp || null
}

/**
 * Check if user has specific scope
 */
export function hasScope(scopes: string[], requiredScope: string): boolean {
  // system:admin grants all permissions
  if (scopes.includes("system:admin")) {
    return true
  }

  return scopes.includes(requiredScope)
}

/**
 * Check if user has any of the required scopes
 */
export function hasAnyScope(
  scopes: string[],
  requiredScopes: string[],
): boolean {
  // system:admin grants all permissions
  if (scopes.includes("system:admin")) {
    return true
  }

  return requiredScopes.some((scope) => scopes.includes(scope))
}

/**
 * Check if user has all required scopes
 */
export function hasAllScopes(
  scopes: string[],
  requiredScopes: string[],
): boolean {
  // system:admin grants all permissions
  if (scopes.includes("system:admin")) {
    return true
  }

  return requiredScopes.every((scope) => scopes.includes(scope))
}

/**
 * Check if user is admin
 */
export function isAdmin(scopes: string[]): boolean {
  return scopes.includes("system:admin")
}

/**
 * Get time until token expires in seconds
 */
export function getTimeUntilExpiry(token: string): number | null {
  const payload = decodeJWT(token)
  if (!payload || !payload.exp) {
    return null
  }

  const now = Math.floor(Date.now() / 1000)
  return Math.max(0, payload.exp - now)
}

/**
 * Check if token needs refresh (expires within 5 minutes)
 */
export function needsRefresh(token: string): boolean {
  const timeUntilExpiry = getTimeUntilExpiry(token)
  if (timeUntilExpiry === null) {
    return true
  }

  // Refresh if expires within 5 minutes (300 seconds)
  return timeUntilExpiry < 300
}

/**
 * Validate JWT token structure (basic validation)
 */
export function isValidJWT(token: string): boolean {
  if (!token || typeof token !== "string") {
    return false
  }

  const parts = token.split(".")
  if (parts.length !== 3) {
    return false
  }

  try {
    const payload = decodeJWT(token)
    return !!(payload?.sub && payload.exp)
  } catch {
    return false
  }
}
